import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, X, Loader2, Wrench, Save, Search } from 'lucide-react';
import { openNativeCamera, fileToDataUrl } from '../../utils/camera.utils';
import { Camera as CameraComponent } from '../Camera';
import { isIOS } from '../../utils/platform.utils';

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface Equipment {
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string;
  equipment_type: string;
  status: string;
}

const ServiceLog: React.FC = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  const [allEquipment, setAllEquipment] = useState<Equipment[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredEquipment, setFilteredEquipment] = useState<Equipment[]>([]);

  // Form state
  const [issueDescription, setIssueDescription] = useState('');
  const [repairPerformed, setRepairPerformed] = useState('');
  const [partsReplaced, setPartsReplaced] = useState('');
  const [laborHours, setLaborHours] = useState('');
  const [estimatedCost, setEstimatedCost] = useState('');
  const [mechanicNotes, setMechanicNotes] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [cameraCooldown, setCameraCooldown] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const translations = {
    en: {
      title: 'Service Log',
      selectEquipment: 'Select Equipment',
      searchPlaceholder: 'Search by name, number, or type...',
      whatWasBroken: 'What was broken or needed repair?',
      whatWorkPerformed: 'What work was performed?',
      partsReplaced: 'Parts Replaced',
      laborHours: 'Labor Hours',
      estimatedCost: 'Estimated Cost ($)',
      additionalNotes: 'Additional Notes',
      photos: 'Photos (Optional)',
      takePhoto: 'Take/Add Photo',
      photosAdded: 'photo(s) added',
      cancel: 'Cancel',
      submitRepair: 'Submit Repair',
      submitting: 'Submitting...',
      successTitle: 'Repair Logged!',
      successMessage: 'has been documented successfully.',
      backToDashboard: 'Back to Dashboard',
      requiredField: '*',
      optional: 'Optional',
      additionalDetails: 'Additional Details (Optional)',
      noEquipment: 'No equipment found',
      selectFirst: 'Select equipment to log a repair',
      errorRequired: 'Please complete all required fields'
    },
    es: {
      title: 'Registro de Servicio',
      selectEquipment: 'Seleccionar Equipo',
      searchPlaceholder: 'Buscar por nombre, número o tipo...',
      whatWasBroken: '¿Qué estaba roto o necesitaba reparación?',
      whatWorkPerformed: '¿Qué trabajo se realizó?',
      partsReplaced: 'Partes Reemplazadas',
      laborHours: 'Horas de Trabajo',
      estimatedCost: 'Costo Estimado ($)',
      additionalNotes: 'Notas Adicionales',
      photos: 'Fotos (Opcional)',
      takePhoto: 'Tomar/Agregar Foto',
      photosAdded: 'foto(s) agregada(s)',
      cancel: 'Cancelar',
      submitRepair: 'Enviar Reparación',
      submitting: 'Enviando...',
      successTitle: '¡Reparación Registrada!',
      successMessage: 'ha sido documentado exitosamente.',
      backToDashboard: 'Volver al Panel',
      requiredField: '*',
      optional: 'Opcional',
      additionalDetails: 'Detalles Adicionales (Opcional)',
      noEquipment: 'No se encontró equipo',
      selectFirst: 'Seleccione equipo para registrar una reparación',
      errorRequired: 'Por favor complete todos los campos requeridos'
    }
  };

  const t = translations[language];

  // Styles matching other modules
  const styles = {
    container: {
      padding: '20px',
      paddingBottom: '100px',
      maxWidth: '800px',
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      padding: '16px',
      marginBottom: '16px'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#ea580c',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      marginBottom: '12px',
      padding: '4px 0'
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    iconCircle: {
      width: '48px',
      height: '48px',
      backgroundColor: '#ea580c',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    headerTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '4px'
    },
    headerSubtitle: {
      fontSize: '14px',
      color: '#64748b'
    },
    content: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    },
    searchSection: {
      padding: '20px',
      borderBottom: '2px solid #f1f5f9'
    },
    searchContainer: {
      position: 'relative' as const
    },
    searchInput: {
      width: '100%',
      padding: '14px 14px 14px 44px',
      fontSize: '20px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      boxSizing: 'border-box' as const,
      backgroundColor: 'white',
      fontWeight: '500' as const
    },
    searchIcon: {
      position: 'absolute' as const,
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#94a3b8'
    },
    dropdown: {
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      right: 0,
      marginTop: '4px',
      backgroundColor: 'white',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      maxHeight: '300px',
      overflowY: 'auto' as const,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 1000
    },
    dropdownItem: {
      padding: '14px',
      cursor: 'pointer',
      borderBottom: '1px solid #f1f5f9',
      transition: 'background-color 0.2s'
    },
    dropdownItemName: {
      fontWeight: '600' as const,
      color: '#1e293b',
      marginBottom: '6px',
      fontSize: '18px'
    },
    dropdownItemDetails: {
      fontSize: '15px',
      color: '#64748b'
    },
    selectedEquipment: {
      marginTop: '12px',
      padding: '12px',
      backgroundColor: '#f8fafc',
      border: '2px solid #e2e8f0',
      borderRadius: '8px'
    },
    selectedEquipmentName: {
      fontWeight: '600' as const,
      color: '#1e293b',
      fontSize: '16px',
      marginBottom: '4px'
    },
    selectedEquipmentDetails: {
      fontSize: '14px',
      color: '#64748b'
    },
    formSection: {
      padding: '20px',
      borderBottom: '2px solid #f1f5f9'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#475569',
      marginBottom: '6px'
    },
    requiredLabel: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#475569',
      marginBottom: '6px'
    } as React.CSSProperties,
    input: {
      width: '100%',
      padding: '10px 12px',
      fontSize: '16px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      boxSizing: 'border-box' as const
    },
    textarea: {
      width: '100%',
      padding: '10px 12px',
      fontSize: '16px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      minHeight: '80px',
      fontFamily: 'inherit',
      resize: 'vertical' as const,
      boxSizing: 'border-box' as const
    },
    photoSection: {
      padding: '20px',
      borderBottom: '2px solid #f1f5f9'
    },
    photoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: '12px',
      marginBottom: '12px'
    },
    photoPreview: {
      position: 'relative' as const,
      paddingTop: '100%',
      borderRadius: '8px',
      overflow: 'hidden',
      border: '2px solid #e2e8f0'
    },
    photoImg: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const
    },
    removePhotoButton: {
      position: 'absolute' as const,
      top: '6px',
      right: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '28px',
      height: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
    },
    photoButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      padding: '12px 20px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px'
    },
    bottomBar: {
      backgroundColor: 'white',
      borderTop: '2px solid #e2e8f0',
      padding: '16px 20px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '12px',
      marginTop: '20px',
      borderRadius: '0 0 12px 12px',
      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.05)'
    },
    saveButton: {
      backgroundColor: '#ea580c',
      color: 'white',
      border: 'none',
      padding: '10px 24px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      opacity: selectedEquipment ? 1 : 0.5,
      pointerEvents: selectedEquipment ? 'auto' as const : 'none' as const,
      boxShadow: '0 2px 4px rgba(234, 88, 12, 0.3)',
      minWidth: '140px'
    },
    cancelButton: {
      backgroundColor: '#94a3b8',
      color: 'white',
      border: 'none',
      padding: '10px 24px',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: '0 2px 4px rgba(148, 163, 184, 0.3)',
      minWidth: '100px'
    },
    successOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    successCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '32px',
      textAlign: 'center' as const,
      maxWidth: '400px',
      width: '100%'
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    emptyState: {
      textAlign: 'center' as const,
      padding: '40px 20px',
      color: '#64748b'
    }
  };

  // Load equipment list
  useEffect(() => {
    loadEquipment();

    const langData = localStorage.getItem('language');
    if (langData === 'en' || langData === 'es') {
      setLanguage(langData);
    }

    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };
    window.addEventListener('languageChange', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  // Filter equipment based on search text
  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredEquipment(allEquipment);
    } else {
      const search = searchText.toLowerCase();
      const filtered = allEquipment.filter(eq =>
        eq.equipment_name.toLowerCase().includes(search) ||
        eq.bradanna_equipment_number.toLowerCase().includes(search) ||
        eq.equipment_type.toLowerCase().includes(search)
      );
      setFilteredEquipment(filtered);
    }
  }, [searchText, allEquipment]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-search-container]')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadEquipment = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAllEquipment(data.data);
      }
    } catch (error) {
      console.error('Error loading equipment:', error);
    }
  };

  const handleSelectEquipment = (equipment: Equipment) => {
    setSelectedEquipment(equipment);
    setSearchText(`${equipment.equipment_name} (#${equipment.bradanna_equipment_number})`);
    setShowDropdown(false);
  };

  const handleTakePhoto = async () => {
    if (cameraCooldown) return;

    if (isIOS()) {
      // iOS: Use native camera
      try {
        setCameraCooldown(true);
        await openNativeCamera(async (file) => {
          if (file && file instanceof File) {
            const previewUrl = await fileToDataUrl(file);
            setPhotos([...photos, file]);
            setPhotoPreviewUrls([...photoPreviewUrls, previewUrl]);
          }
        });
        setTimeout(() => setCameraCooldown(false), 1000);
      } catch (err) {
        console.error('Error capturing photo:', err);
        setError('Failed to capture photo. Please try again.');
        setCameraCooldown(false);
      }
    } else {
      // Android: Use custom camera component
      setShowCamera(true);
    }
  };

  const handleCameraCapture = async (imageDataUrl: string) => {
    setShowCamera(false);

    try {
      // Convert base64 to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });

      const previewUrl = await fileToDataUrl(file);
      setPhotos([...photos, file]);
      setPhotoPreviewUrls([...photoPreviewUrls, previewUrl]);

      setCameraCooldown(true);
      setTimeout(() => setCameraCooldown(false), 1000);
    } catch (err) {
      console.error('Error processing camera capture:', err);
      setError('Failed to capture photo. Please try again.');
      setCameraCooldown(false);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    setCameraCooldown(true);
    setTimeout(() => setCameraCooldown(false), 500);
  };

  const removePhoto = (index: number) => {
    const newPhotos = [...photos];
    const newPreviewUrls = [...photoPreviewUrls];

    URL.revokeObjectURL(newPreviewUrls[index]);

    newPhotos.splice(index, 1);
    newPreviewUrls.splice(index, 1);

    setPhotos(newPhotos);
    setPhotoPreviewUrls(newPreviewUrls);
  };

  const handleSubmit = async () => {
    if (!selectedEquipment) {
      setError('Please select equipment');
      return;
    }

    // Validation
    if (!issueDescription.trim()) {
      setError('Please describe what was broken or needed repair');
      return;
    }

    if (!repairPerformed.trim()) {
      setError('Please describe what work was performed');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('equipment_id', selectedEquipment.equipment_id.toString());
      formData.append('issue_description', issueDescription);
      formData.append('repair_performed', repairPerformed);
      if (partsReplaced.trim()) formData.append('parts_replaced', partsReplaced);
      if (laborHours) formData.append('labor_hours', laborHours);
      if (estimatedCost) formData.append('estimated_cost', estimatedCost);
      if (mechanicNotes.trim()) formData.append('mechanic_notes', mechanicNotes);
      formData.append('status', 'completed');

      photos.forEach((photo) => {
        formData.append('photos', photo);
      });

      const response = await fetch(`${API_BASE_URL}/api/equipment/repairs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Failed to submit repair');
      }
    } catch (err: any) {
      console.error('Error submitting repair:', err);
      setError(err.message || 'Failed to submit repair');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedEquipment(null);
    setSearchText('');
    setIssueDescription('');
    setRepairPerformed('');
    setPartsReplaced('');
    setLaborHours('');
    setEstimatedCost('');
    setMechanicNotes('');
    setPhotos([]);
    setPhotoPreviewUrls([]);
    setError(null);
    setSuccess(false);
  };

  if (success && selectedEquipment) {
    return (
      <div style={styles.successOverlay}>
        <div style={styles.successCard}>
          <Wrench size={64} style={{ color: '#ea580c', margin: '0 auto 16px auto' }} />
          <h2 style={{ margin: '0 0 8px 0', color: '#ea580c' }}>{t.successTitle}</h2>
          <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>
            {selectedEquipment.equipment_name} {t.successMessage}
          </p>
          <button onClick={() => navigate(-1)} style={styles.saveButton}>
            {t.backToDashboard}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.iconCircle}>
            <Wrench size={24} color="white" />
          </div>
          <div>
            <h1 style={styles.headerTitle}>{t.title}</h1>
            <p style={styles.headerSubtitle}>
              {language === 'es' ? 'Registrar una reparación de equipo' : 'Log an equipment repair'}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Equipment Selection */}
        <div style={styles.searchSection}>
          <label style={styles.label}>{t.selectEquipment} *</label>
          <div style={styles.searchContainer} data-search-container>
            <div style={styles.searchIcon}>
              <Search size={18} />
            </div>
            <input
              type="text"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => {
                if (!selectedEquipment) {
                  setShowDropdown(true);
                }
              }}
              placeholder={t.searchPlaceholder}
              style={styles.searchInput}
            />
            {showDropdown && filteredEquipment.length > 0 && (
              <div style={styles.dropdown}>
                {filteredEquipment.map((eq) => (
                  <div
                    key={eq.equipment_id}
                    onClick={() => handleSelectEquipment(eq)}
                    style={styles.dropdownItem}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <div style={styles.dropdownItemName}>
                      {eq.equipment_name} (#{eq.bradanna_equipment_number})
                    </div>
                    <div style={styles.dropdownItemDetails}>
                      {eq.equipment_type}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showDropdown && searchText && filteredEquipment.length === 0 && (
              <div style={styles.dropdown}>
                <div style={{ ...styles.dropdownItem, textAlign: 'center', color: '#9ca3af' }}>
                  {t.noEquipment}
                </div>
              </div>
            )}
          </div>

          {selectedEquipment && (
            <div style={styles.selectedEquipment}>
              <div style={styles.selectedEquipmentName}>
                {selectedEquipment.equipment_name}
              </div>
              <div style={styles.selectedEquipmentDetails}>
                #{selectedEquipment.bradanna_equipment_number} • {selectedEquipment.equipment_type}
              </div>
            </div>
          )}
        </div>

        {/* Show form only if equipment is selected */}
        {selectedEquipment ? (
          <>
            {/* Required Fields Section */}
            <div style={styles.formSection}>
              <div style={styles.formGroup}>
                <label style={styles.requiredLabel}>
                  {t.whatWasBroken} {t.requiredField}
                </label>
                <textarea
                  value={issueDescription}
                  onChange={(e) => setIssueDescription(e.target.value)}
                  style={styles.textarea}
                  placeholder="e.g., AC Compressor not working - no cold air"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.requiredLabel}>
                  {t.whatWorkPerformed} {t.requiredField}
                </label>
                <textarea
                  value={repairPerformed}
                  onChange={(e) => setRepairPerformed(e.target.value)}
                  style={styles.textarea}
                  placeholder="e.g., Replaced AC compressor and recharged system"
                />
              </div>
            </div>

            {/* Optional Fields Section */}
            <div style={styles.formSection}>
              <div style={styles.sectionTitle}>{t.additionalDetails}</div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t.partsReplaced}</label>
                <textarea
                  value={partsReplaced}
                  onChange={(e) => setPartsReplaced(e.target.value)}
                  style={styles.textarea}
                  placeholder="e.g., AC Compressor (Part# AC-12345)"
                  rows={2}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.laborHours}</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={laborHours}
                    onChange={(e) => setLaborHours(e.target.value)}
                    style={styles.input}
                    placeholder="e.g., 4.5"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t.estimatedCost}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={estimatedCost}
                    onChange={(e) => setEstimatedCost(e.target.value)}
                    style={styles.input}
                    placeholder="e.g., 850.00"
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t.additionalNotes}</label>
                <textarea
                  value={mechanicNotes}
                  onChange={(e) => setMechanicNotes(e.target.value)}
                  style={styles.textarea}
                  placeholder="e.g., Tested system after installation..."
                  rows={2}
                />
              </div>
            </div>

            {/* Photos Section */}
            <div style={styles.photoSection}>
              <div style={styles.sectionTitle}>
                <Camera size={20} />
                {t.photos}
              </div>

              {photoPreviewUrls.length > 0 && (
                <div style={styles.photoGrid}>
                  {photoPreviewUrls.map((url, index) => (
                    <div key={index} style={styles.photoPreview}>
                      <img src={url} alt={`Repair photo ${index + 1}`} style={styles.photoImg} />
                      <button onClick={() => removePhoto(index)} style={styles.removePhotoButton}>
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={handleTakePhoto}
                disabled={cameraCooldown}
                style={{
                  ...styles.photoButton,
                  opacity: cameraCooldown ? 0.5 : 1,
                  cursor: cameraCooldown ? 'not-allowed' : 'pointer'
                }}
              >
                <Camera size={18} />
                <span>{t.takePhoto}</span>
              </button>

              {photos.length > 0 && (
                <p style={{ fontSize: '14px', color: '#64748b', marginTop: '12px' }}>
                  {photos.length} {t.photosAdded}
                </p>
              )}
            </div>
          </>
        ) : (
          <div style={styles.emptyState}>
            <Wrench size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
            <p>{t.selectFirst}</p>
          </div>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div style={styles.bottomBar}>
        <button onClick={handleReset} style={styles.cancelButton}>
          <X size={20} />
          {t.cancel}
        </button>
        <button onClick={handleSubmit} disabled={isSubmitting || !selectedEquipment} style={styles.saveButton}>
          {isSubmitting ? (
            <>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
              {t.submitting}
            </>
          ) : (
            <>
              <Save size={20} />
              {t.submitRepair}
            </>
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '20px',
          right: '20px',
          backgroundColor: '#fef2f2',
          border: '2px solid #fca5a5',
          color: '#dc2626',
          padding: '12px 16px',
          borderRadius: '8px',
          zIndex: 1000,
          textAlign: 'left',
          fontSize: '14px',
          boxShadow: '0 4px 12px rgba(220, 38, 38, 0.15)'
        }}>
          <button
            onClick={() => setError(null)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'none',
              border: 'none',
              color: '#dc2626',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={16} />
          </button>
          {error}
        </div>
      )}

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Camera Component (Android only) */}
      {showCamera && (
        <CameraComponent
          onCapture={handleCameraCapture}
          onClose={handleCameraClose}
        />
      )}
    </div>
  );
};

export default ServiceLog;
